
import { ref, query, orderByKey, limitToLast, onValue, get } from "firebase/database";
import { db } from "../../../functions/api_firebase";
import { refrescarNits } from "../../../functions/variables_globales";
import { nitsVisibles } from "./consultas_sos";
import { normalizarNodo, normalizarEstado, idSOS } from "./datos_sos";
import {
  crearMemoriaVigilancia,
  calcularVigilancia,
  sembrarConocidas,
  olvidarAlerta,
  VENTANA_POR_NIT,
} from "./logica_vigilancia";
import {
  prepararAlertas,
  sonarAlerta,
  detenerSonido,
  notificar,
  iniciarParpadeoTitulo,
  detenerParpadeoTitulo,
} from "../../../functions/notificaciones_sos";

export { VENTANA_POR_NIT };


const MINUTOS_RECORDATORIO = 3;

const REPETICIONES_SONIDO_NUEVO = 3;

const MINUTOS_REFRESCO_NITS = 30;

const MS_BACKOFF_BASE = 2000;
const MS_BACKOFF_MAX = 60000;

const MS_PLAZO_ARRANQUE = 10000;

const MS_AGRUPACION = 120;

const MS_REVERIFICACION = 60000;

const MS_MIN_ENTRE_REVALIDACIONES = 60000;

const MS_OCULTA_PARA_REVALIDAR = 5 * 60000;

const MS_DESCONEXION_PARA_REVALIDAR = 30000;


const ESTADO_INICIAL = {
  listo: false,
  conectado: true,
  pendientes: [],
  nuevas: [],
  nitsVigilados: 0,
  silenciadoHasta: 0,
  error: null,
};

export const iniciarVigilanciaSOS = ({ alEstado, alAbrirSOS } = {}) => {
  let activo = true;

  const porNit = new Map();
  const listeners = new Map();
  const reintentos = new Map();
  const memoria = crearMemoriaVigilancia();

  const verificaciones = new Map();

  const nitsSembrados = new Set();

  let sincronizacionInicialCompleta = false;
  let faltantes = 0;

  let ultimaRevalidacion = 0;
  let ocultaDesde = 0;
  let desconectadaDesde = 0;

  let temporizadorEmision = null;
  let temporizadorRecordatorio = null;
  let temporizadorNits = null;
  let temporizadorArranque = null;
  let temporizadorReintentoArranque = null;
  let intentosArranque = 0;
  let cancelarConexion = null;

  let avisoVisualSuprimido = false;

  let estado = { ...ESTADO_INICIAL };

  prepararAlertas();


  const publicar = () => {
    if (!activo) return;
    try {
      alEstado?.({ ...estado });
    } catch (error) {
      console.error("Vigilancia SOS: error publicando estado", error);
    }
  };

  const fijar = (cambios) => {
    estado = { ...estado, ...cambios };
    publicar();
  };


  const silenciado = () => Date.now() < estado.silenciadoHasta;

  const describir = (alerta) => {
    if (!alerta) return "Revisa el módulo SOS.";
    const partes = [alerta.cliente, alerta.nombre_completo, alerta.tipo]
      .map((v) => (v && v !== "N/A" ? String(v) : ""))
      .filter(Boolean);
    return partes.length ? partes.join(" · ") : "Revisa el módulo SOS.";
  };

  const avisar = ({ esNueva, nuevas = [] }) => {
    if (!activo || silenciado()) return;

    try {
      const total = estado.pendientes.length;

      if (esNueva) {
        sonarAlerta(REPETICIONES_SONIDO_NUEVO);
        notificar({
          titulo: nuevas.length > 1
            ? `🚨 ${nuevas.length} alertas SOS nuevas`
            : "🚨 ¡Alerta SOS!",
          cuerpo: describir(nuevas[0]),
          alHacerClic: alAbrirSOS,
        });
      } else {
        sonarAlerta(1);
        notificar({
          titulo: `🚨 ${total} SOS sin atender`,
          cuerpo: "Siguen pendientes de gestión.",
          alHacerClic: alAbrirSOS,
        });
      }
    } catch (error) {
      console.error("Vigilancia SOS: error emitiendo la alerta", error);
    }
  };

  const sincronizarParpadeo = () => {
    try {
      const total = estado.pendientes.length;
      if (total > 0 && !silenciado() && !avisoVisualSuprimido) {
        iniciarParpadeoTitulo(`🚨 ${total} SOS`);
      } else {
        detenerParpadeoTitulo();
      }
    } catch (error) {
      console.error("Vigilancia SOS: error con el parpadeo del título", error);
    }
  };


  const verificarFueraDeVentana = async (alerta) => {
    const id = idSOS(alerta);
    const ahora = Date.now();

    if ((verificaciones.get(id) || 0) + MS_REVERIFICACION > ahora) return;
    verificaciones.set(id, ahora);

    try {
      const snap = await get(ref(db, `/sos/${alerta.nit}/${alerta.key}/estado`));
      if (!activo) return;

      if (!snap.exists() || normalizarEstado(snap.val()) === "Cerrado") {
        olvidarAlerta(memoria, id);
        verificaciones.delete(id);
        emitir();
      }
    } catch (error) {
      console.warn(`Vigilancia SOS: no se pudo verificar ${id}`, error);
    }
  };

  const recalcular = () => {
    if (!activo) return;

    const { pendientes, nuevas, recienLlegadas, fueraDeVentana, aviso } =
      calcularVigilancia(memoria, [].concat(...porNit.values()));

    fueraDeVentana.forEach(verificarFueraDeVentana);

    estado = { ...estado, listo: true, pendientes, nuevas };

    if (aviso) {
      avisar({ esNueva: aviso.tipo === "nueva", nuevas: recienLlegadas });
    }

    sincronizarParpadeo();
    publicar();
  };

  const emitir = () => {
    if (!activo) return;
    clearTimeout(temporizadorEmision);
    temporizadorEmision = setTimeout(recalcular, MS_AGRUPACION);
  };

  const marcarRespuesta = () => {
    if (sincronizacionInicialCompleta) return emitir();

    faltantes -= 1;
    if (faltantes <= 0) {
      sincronizacionInicialCompleta = true;
      clearTimeout(temporizadorArranque);
      emitir();
    }
  };


  const programarReintento = (nit) => {
    if (!activo) return;

    const info = reintentos.get(nit) || { intentos: 0, timer: null };
    clearTimeout(info.timer);
    info.intentos += 1;

    const espera = Math.min(
      MS_BACKOFF_BASE * 2 ** (info.intentos - 1),
      MS_BACKOFF_MAX
    );

    info.timer = setTimeout(() => {
      if (!activo) return;
      listeners.delete(nit);
      escucharNit(nit);
    }, espera);

    reintentos.set(nit, info);
  };

  function escucharNit(nit) {
    if (!activo || listeners.has(nit)) return;

    const consulta = query(ref(db, `/sos/${nit}`), orderByKey(), limitToLast(VENTANA_POR_NIT));

    try {
      const cancelar = onValue(
        consulta,
        (snap) => {
          if (!activo) return;
          const info = reintentos.get(nit);
          if (info) {
            clearTimeout(info.timer);
            reintentos.delete(nit);
          }

          const filas = normalizarNodo(snap.val(), nit);

          if (!nitsSembrados.has(nit)) {
            nitsSembrados.add(nit);
            sembrarConocidas(memoria, filas);
          }

          porNit.set(nit, filas);
          marcarRespuesta();
        },
        (error) => {
          if (!activo) return;
          console.error(`Vigilancia SOS: falló la escucha de ${nit}`, error);
          porNit.set(nit, porNit.get(nit) || []);
          listeners.delete(nit);
          marcarRespuesta();
          programarReintento(nit);
        }
      );

      listeners.set(nit, cancelar);
    } catch (error) {
      console.error(`Vigilancia SOS: no se pudo abrir la escucha de ${nit}`, error);
      marcarRespuesta();
      programarReintento(nit);
    }
  }

  const dejarDeEscuchar = (nit) => {
    const cancelar = listeners.get(nit);
    if (cancelar) {
      try { cancelar(); } catch {  }
    }
    listeners.delete(nit);

    const info = reintentos.get(nit);
    if (info) clearTimeout(info.timer);
    reintentos.delete(nit);
  };


  const arrancar = async () => {
    if (!activo) return;

    try {
      const nits = await nitsVisibles();
      if (!activo) return;

      if (!nits.length) {
        fijar({ listo: true, error: "No hay clientes que vigilar." });
        return;
      }

      intentosArranque = 0;
      faltantes = nits.length;
      fijar({ nitsVigilados: nits.length, error: null });

      temporizadorArranque = setTimeout(() => {
        if (!activo || sincronizacionInicialCompleta) return;
        sincronizacionInicialCompleta = true;
        emitir();
      }, MS_PLAZO_ARRANQUE);

      nits.forEach(escucharNit);
    } catch (error) {
      console.error("Vigilancia SOS: no se pudo arrancar", error);
      if (!activo) return;

      intentosArranque += 1;
      const espera = Math.min(
        MS_BACKOFF_BASE * 2 ** (intentosArranque - 1),
        MS_BACKOFF_MAX
      );
      fijar({ error: "Reintentando conectar con la vigilancia de SOS…" });
      temporizadorReintentoArranque = setTimeout(arrancar, espera);
    }
  };


  const revalidarVentanas = async () => {
    if (!activo) return;

    await Promise.all(
      [...listeners.keys()].map(async (nit) => {
        try {
          const snap = await get(query(ref(db, `/sos/${nit}`), orderByKey(), limitToLast(VENTANA_POR_NIT)));
          if (!activo) return;
          porNit.set(nit, normalizarNodo(snap.val(), nit));
        } catch (error) {
          console.warn(`Vigilancia SOS: no se pudo revalidar ${nit}`, error);
        }
      })
    );

    if (activo) recalcular();
  };

  const revalidarSiHaceFalta = (motivo) => {
    if (!activo) return;
    const ahora = Date.now();
    if (ahora - ultimaRevalidacion < MS_MIN_ENTRE_REVALIDACIONES) return;
    ultimaRevalidacion = ahora;
    revalidarVentanas();
  };

  const alCambiarVisibilidad = () => {
    if (typeof document === "undefined") return;

    if (document.visibilityState !== "visible") {
      ocultaDesde = Date.now();
      return;
    }

    const tiempoOculta = ocultaDesde ? Date.now() - ocultaDesde : 0;
    ocultaDesde = 0;
    if (tiempoOculta > MS_OCULTA_PARA_REVALIDAR) {
      revalidarSiHaceFalta("la pestaña volvió tras estar oculta");
    }
  };

  const alReanudar = () => revalidarSiHaceFalta("el navegador reanudó la pestaña");

  const alVolverLaRed = () => revalidarSiHaceFalta("volvió la red");

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", alCambiarVisibilidad);
    document.addEventListener("resume", alReanudar);
  }
  if (typeof window !== "undefined") {
    window.addEventListener("online", alVolverLaRed);
  }


  try {
    cancelarConexion = onValue(ref(db, ".info/connected"), (snap) => {
      if (!activo) return;

      const conectado = snap.val() === true;

      if (!conectado) {
        desconectadaDesde = desconectadaDesde || Date.now();
      } else if (desconectadaDesde) {
        const fuera = Date.now() - desconectadaDesde;
        desconectadaDesde = 0;
        if (fuera > MS_DESCONEXION_PARA_REVALIDAR) {
          revalidarSiHaceFalta("se recuperó la conexión");
        }
      }

      fijar({ conectado });
    });
  } catch (error) {
    console.warn("Vigilancia SOS: no se pudo vigilar el estado de conexión", error);
  }


  temporizadorRecordatorio = setInterval(() => {
    if (!activo || silenciado()) return;
    if (!estado.pendientes.length) return;
    avisar({ esNueva: false });
    sincronizarParpadeo();
  }, MINUTOS_RECORDATORIO * 60000);


  temporizadorNits = setInterval(async () => {
    if (!activo) return;

    if (estado.nitsVigilados <= 1) return;

    try {
      await refrescarNits();
      const nits = await nitsVisibles();
      if (!activo || !nits.length) return;

      const vigentes = new Set(nits);

      nits.forEach((nit) => { if (!listeners.has(nit)) escucharNit(nit); });
      [...listeners.keys()].forEach((nit) => {
        if (!vigentes.has(nit)) {
          dejarDeEscuchar(nit);
          porNit.delete(nit);
        }
      });

      fijar({ nitsVigilados: nits.length });
      emitir();
    } catch (error) {
      console.warn("Vigilancia SOS: no se pudo refrescar la lista de NITs", error);
    }
  }, MINUTOS_REFRESCO_NITS * 60000);

  arrancar();


  return {
    silenciar(minutos) {
      detenerSonido();
      detenerParpadeoTitulo();
      fijar({ silenciadoHasta: Date.now() + minutos * 60000 });
    },

    reanudar() {
      fijar({ silenciadoHasta: 0 });
      sincronizarParpadeo();
    },

    suprimirAvisoVisual(valor) {
      avisoVisualSuprimido = !!valor;
      sincronizarParpadeo();
    },

    async revalidar() {
      ultimaRevalidacion = Date.now();
      await revalidarVentanas();
    },

    detener() {
      activo = false;

      clearTimeout(temporizadorEmision);
      clearTimeout(temporizadorArranque);
      clearTimeout(temporizadorReintentoArranque);
      clearInterval(temporizadorRecordatorio);
      clearInterval(temporizadorNits);

      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", alCambiarVisibilidad);
        document.removeEventListener("resume", alReanudar);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("online", alVolverLaRed);
      }

      [...listeners.keys()].forEach(dejarDeEscuchar);
      porNit.clear();

      if (cancelarConexion) {
        try { cancelarConexion(); } catch {  }
      }

      detenerSonido();
      detenerParpadeoTitulo();
    },
  };
};
